import axios from 'axios';

// Create an instance of axios
const BASE_URL = process.env.VUE_APP_BASE_URL; // Use environment variable or default to localhost
const TIMEOUT = 10000; // Set a timeout of 10 seconds

// Create an instance of axios
const axiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
  },
});
// Add a response interceptor
if (axiosInstance && axiosInstance.interceptors) {
  // Add a response interceptor
  axiosInstance.interceptors.request.use(
    config => {
      const token = localStorage.getItem('token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    error => {
      return Promise.reject(error);
    }
  );
  
  // Add response interceptor to handle token refresh
  let isRefreshing = false;
  let failedQueue = [];

  const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
      if (error) {
        prom.reject(error);
      } else {
        prom.resolve(token);
      }
    });
    
    failedQueue = [];
  };

  axiosInstance.interceptors.response.use(
    response => response,
    async error => {
      const originalRequest = error.config;
      const isLoginPage = window.location.pathname.includes('/login');
      
      // Handle 401 errors (unauthorized/expired token)
      if (error.response?.status === 401 && !originalRequest._retry) {
        // Check if we're already refreshing to prevent multiple refresh requests
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              originalRequest.headers['Authorization'] = `Bearer ${token}`;
              return axiosInstance(originalRequest);
            })
            .catch(err => {
              return Promise.reject(err);
            });
        }
  
        originalRequest._retry = true;
        isRefreshing = true;
        
        // Check if we have a refresh token
        const refreshToken = localStorage.getItem('refreshToken');
        
        if (!refreshToken) {
          isRefreshing = false;
          // Clear storage
          localStorage.removeItem('token');
          localStorage.removeItem('role');
          localStorage.removeItem('userId');
          
          // Only redirect if not already on login page
          if (!isLoginPage) {
            console.log('Redirecting to login page');
            window.location.href = '/login';
          } else {
            console.log('Already on login page, not redirecting');
          }
          return Promise.reject(error);
        }
        
        try {
          // Try to get a new token
          const response = await axios.post(
            `${axiosInstance.defaults.baseURL}/refresh-token`, 
            { refreshToken }
          );
          
          // If successful, update tokens in storage
          const newToken = response.data.token;
          localStorage.setItem('token', newToken);
          
          // Update axios header
          axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          
          // Process any pending requests
          processQueue(null, newToken);
          isRefreshing = false;
          
          // Retry the original request
          return axiosInstance(originalRequest);
        } catch (err) {
          // If refresh fails
          processQueue(err);
          isRefreshing = false;
          console.error('Token refresh failed', err);
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('role');
          localStorage.removeItem('userId');
          
          // Only redirect if not already on login page
          if (!isLoginPage) {
            window.location.href = '/login';
          }
          return Promise.reject(err);
        }
      } else if (error.response?.status === 401 && originalRequest._retry) {
        // If we already tried refreshing and still got a 401, we need to log out
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('role');
        localStorage.removeItem('userId');
        
        // Only redirect if not already on login page
        if (!isLoginPage) {
          window.location.href = '/login';
        }
      }
      
      // If it's not a 401 error or we've already handled it, just reject the promise
      return Promise.reject(error);
    }
  );
}

export default axiosInstance;

